import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { getDocumentDict } from '@documents/selectors/documents.selectors';
import { Store } from '@ngrx/store';
import { UtilsService } from '@shared/services/utils.service';

import { filter, map, Observable, take } from 'rxjs';
import environment from '../../../../environments/environment';

import {
  IMessageReqBody,
  IMessageResponse,
  IMessageResults,
  MessageEntityType,
  Thread,
  ThreadType,
} from '@data-access/submissions/types';
import {
  selectAllThreads,
  selectIfExistinThreads,
  selectOpenThreads,
  selectThread,
} from '../redux/submission.selectors';
import {
  createNewThread,
  storeActiveThread,
  storeThreadList as storeThreads,
} from '../redux/submission.actions';

@Injectable({
  providedIn: 'root',
})
export class MessagesService {
  private readonly _http = inject(HttpClient);
  private readonly _store = inject(Store);
  private readonly _utils = inject(UtilsService);

  messages$: Observable<Thread[]> = this._store.select(selectOpenThreads);
  messagesHistory$: Observable<Thread[] | undefined> = this._store
    .select(selectAllThreads)
    .pipe(filter((messages) => !!messages));
  activeThread$: Observable<Thread | undefined> =
    this._store.select(selectThread);

  createMessage(submissionId: string, message: IMessageReqBody) {
    const data = this._utils.convertKeysToSnakeCase(message);
    return this._http.post<IMessageResponse>(
      `${environment.irisApi}/workspace/${submissionId}/message-new/`,
      data,
    );
  }

  replyMessage(
    submissionId: string,
    messageId: string,
    message: IMessageReqBody,
  ) {
    const data = this._utils.convertKeysToSnakeCase(message);
    return this._http.post<IMessageResponse>(
      `${environment.irisApi}/workspace/${submissionId}/message/${messageId}/reply-new/`,
      data,
    );
  }

  getMessages(workspaceId: string) {
    return this._http
      .get<IMessageResults>(
        `${environment.irisApi}/workspace/${workspaceId}/message-new/?workspace_id=${workspaceId}&filter_by=all&page_size=100`,
      )
      .pipe(
        map((response) => {
          const data = this.sortThreadsByResolved(
            this._utils.convertKeysToCamelCase(response.data),
          );
          this._store.dispatch(
            storeThreads({ messages: this.groupMessagesByEntity(data) }),
          );
        }),
      );
  }

  sortThreadsByResolved(messages: Thread[]): Thread[] {
    return messages.sort((a, b) => Number(a.isResolved) - Number(b.isResolved));
  }

  groupMessagesByEntity(threads: Thread[]): Thread[] {
    return threads.map((thread) => {
      return {
        ...thread,
        typeOfThread:
          thread.entityType === MessageEntityType.ANSWER
            ? 'questionMessage'
            : thread.messages?.[0]?.data?.documentTypeName
              ? 'documentRequest'
              : 'documentMessage',
      };
    });
  }

  selectThread(threadId: string, workspaceId: string, entityType: ThreadType) {
    this._store
      .select(selectIfExistinThreads, { entityId: threadId })
      .pipe(take(1))
      .subscribe((thread) => {
        if (thread) {
          this._store.dispatch(storeActiveThread({ threadId }));
          return;
        }
        if (!workspaceId) {
          return;
        }
        const body: Thread = {
          data: {},
          entityId: threadId,
          entityType: entityType,
          messages: [],
          workspaceId: workspaceId,
        };
        this._store.dispatch(createNewThread({ thread: body }));
      });
  }

  getDocumentTypeFromDict(id: string): Observable<string> {
    return this._store.select(getDocumentDict).pipe(
      filter((dict) => !!dict && !!dict[id]),
      map((dict) => dict[id].documentType),
    );
  }

  exportChat(submissionId: string) {
    return this._http.get<Blob>(
      `${environment.irisApi}/workspace/${submissionId}/export-collab-hub-new/`,
      {
        params: { workspace_id: submissionId },
        observe: 'response',
        responseType: 'blob' as 'json',
      },
    );
  }

  exportSingleChat(submissionId: string, entityId: string) {
    return this._http.get<Blob>(
      `${environment.irisApi}/workspace/${submissionId}/export-collab-hub-new/${entityId}/`,
      {
        observe: 'response',
        responseType: 'blob' as 'json',
      },
    );
  }
}
